import { z } from 'zod';
import * as C from './common.js';
import * as A from './auth.js';
import * as O from './org.js';
import * as K from './contacts.js';
import * as R from './relationships.js';
import * as X from './activity.js';
import * as M from './misc.js';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
export interface RouteDef {
  method: HttpMethod;
  path: string;
  query?: z.ZodTypeAny;
  body?: z.ZodTypeAny;
  response: z.ZodTypeAny;
  auth: boolean;
}

const r = (d: RouteDef): RouteDef => d;
const noContent = z.undefined();

/** Single source of truth for REST API v1 (prefix /api/v1). GET never changes state. */
export const routes = {
  login: r({ method: 'POST', path: '/auth/login', body: A.LoginRequest, response: A.LoginResponse, auth: false }),
  acceptInvite: r({ method: 'POST', path: '/auth/accept-invite', body: A.AcceptInviteRequest, response: C.OkResponse, auth: false }),
  logout: r({ method: 'POST', path: '/auth/logout', response: C.OkResponse, auth: true }),
  me: r({ method: 'GET', path: '/auth/me', response: A.Me, auth: true }),

  listVerticals: r({ method: 'GET', path: '/verticals', response: z.object({ items: z.array(O.Vertical) }), auth: true }),
  getVertical: r({ method: 'GET', path: '/verticals/:id', response: O.Vertical, auth: true }),

  listUsers: r({ method: 'GET', path: '/users', response: z.object({ items: z.array(O.User) }), auth: true }),
  inviteUser: r({ method: 'POST', path: '/users/invite', body: O.InviteUserRequest, response: O.InviteUserResponse, auth: true }),
  setMemberships: r({ method: 'PUT', path: '/users/:id/memberships', body: O.SetMembershipsRequest, response: O.User, auth: true }),

  listContacts: r({ method: 'GET', path: '/contacts', query: K.ContactListQuery, response: C.page(K.Contact), auth: true }),
  dedupeContacts: r({ method: 'GET', path: '/contacts/dedupe', query: K.DedupeQuery, response: K.DedupeResponse, auth: true }),
  createContact: r({ method: 'POST', path: '/contacts', body: K.CreateContactRequest, response: K.Contact, auth: true }),
  getContact: r({ method: 'GET', path: '/contacts/:id', response: K.Contact, auth: true }),
  updateContact: r({ method: 'PATCH', path: '/contacts/:id', body: K.UpdateContactRequest, response: K.Contact, auth: true }),

  listAccounts: r({ method: 'GET', path: '/accounts', query: K.AccountListQuery, response: C.page(K.Account), auth: true }),
  createAccount: r({ method: 'POST', path: '/accounts', body: K.CreateAccountRequest, response: K.Account, auth: true }),
  getAccount: r({ method: 'GET', path: '/accounts/:id', response: K.Account, auth: true }),
  updateAccount: r({ method: 'PATCH', path: '/accounts/:id', body: K.UpdateAccountRequest, response: K.Account, auth: true }),

  listRelationships: r({ method: 'GET', path: '/relationships', query: R.RelationshipListQuery, response: C.page(R.Relationship), auth: true }),
  createRelationship: r({ method: 'POST', path: '/relationships', body: R.CreateRelationshipRequest, response: R.Relationship, auth: true }),
  getRelationship: r({ method: 'GET', path: '/relationships/:id', response: R.Relationship, auth: true }),
  updateRelationship: r({ method: 'PATCH', path: '/relationships/:id', body: R.UpdateRelationshipRequest, response: R.Relationship, auth: true }),
  reassignRelationship: r({ method: 'POST', path: '/relationships/:id/reassign', body: R.ReassignRequest, response: R.ReassignResponse, auth: true }),
  assignmentHistory: r({ method: 'GET', path: '/relationships/:id/assignments', response: z.object({ items: z.array(R.AssignmentHistoryItem) }), auth: true }),

  listLog: r({ method: 'GET', path: '/relationships/:id/log', query: X.LogListQuery, response: C.page(X.LogEntry), auth: true }),
  createLog: r({ method: 'POST', path: '/relationships/:id/log', body: X.CreateLogEntryRequest, response: X.LogEntry, auth: true }),

  listCommitments: r({ method: 'GET', path: '/relationships/:id/commitments', response: z.object({ items: z.array(X.Commitment) }), auth: true }),
  createCommitment: r({ method: 'POST', path: '/relationships/:id/commitments', body: X.CreateCommitmentRequest, response: X.Commitment, auth: true }),
  updateCommitment: r({ method: 'PATCH', path: '/commitments/:id', body: X.UpdateCommitmentRequest, response: X.Commitment, auth: true }),

  listDrafts: r({ method: 'GET', path: '/drafts', query: X.DraftListQuery, response: C.page(X.Draft), auth: true }),
  createDraft: r({ method: 'POST', path: '/drafts', body: X.CreateDraftRequest, response: X.Draft, auth: true }),
  updateDraft: r({ method: 'PATCH', path: '/drafts/:id', body: X.UpdateDraftRequest, response: X.Draft, auth: true }),
  markSent: r({ method: 'POST', path: '/drafts/:id/mark-sent', body: X.MarkSentRequest, response: X.MarkSentResponse, auth: true }),

  listMemory: r({ method: 'GET', path: '/memory', query: M.MemoryListQuery, response: C.page(M.MemoryItem), auth: true }),
  deleteMemory: r({ method: 'DELETE', path: '/memory/:id', response: noContent, auth: true }),

  presignUpload: r({ method: 'POST', path: '/files/presign-upload', body: M.PresignUploadRequest, response: M.PresignUploadResponse, auth: true }),
  confirmUpload: r({ method: 'POST', path: '/files/:id/confirm', response: M.FileObject, auth: true }),
  listFiles: r({ method: 'GET', path: '/relationships/:id/files', response: z.object({ items: z.array(M.FileObject) }), auth: true }),
  presignDownload: r({ method: 'POST', path: '/files/:id/presign-download', response: M.PresignDownloadResponse, auth: true }),

  listNotifications: r({ method: 'GET', path: '/notifications', query: M.NotificationListQuery, response: C.page(M.Notification), auth: true }),
  markNotificationRead: r({ method: 'POST', path: '/notifications/:id/read', response: M.Notification, auth: true }),

  today: r({ method: 'GET', path: '/today', query: M.TodayQuery, response: M.TodayBrief, auth: true }),
} as const satisfies Record<string, RouteDef>;

export const API_PREFIX = '/api/v1';
